import { useEffect, useMemo, useState } from 'react';
import { Panel } from '../../../components/panels/Panel';
import { Loader } from '../../../components/loading/Loader';
import { useGetData } from '../../../services/api/api-tools';
import { getInfluxdbData } from '../../../services/api/influxdb-tools';
import {
  filterVACAeroDashboardPieces,
  formatVACAeroDashboardDuration,
  formatVACAeroDashboardIndex,
  formatVACAeroDashboardInfluxAlerts,
  formatVACAeroDashboardInfluxPieces,
  formatVACAeroDashboardPercentage,
  getVACAeroDashboardData,
  getVACAeroDashboardDefaultFilters,
} from '../../../services/dashboard/vacaero-dashboard-service';
import { VACAeroDashboardKpi, VACAeroDashboardNoDataNotice, VACAeroDashboardScoreKpi } from './components/VACAeroDashboardBlocks';
import {
  VACAeroDashboardAlertDistribution,
  VACAeroDashboardComplianceEvolutionChart,
  VACAeroDashboardCycleTimeEvolutionChart,
  VACAeroDashboardMasteryEvolutionChart,
} from './components/VACAeroDashboardCharts';
import { VACAeroDashboardFilters } from './components/VACAeroDashboardFilters';
import { VACAeroDashboardProcessesTable } from './components/VACAeroDashboardProcessesTable';

const HISTORY_START = '1970-01-01T00:00:00Z';

const PIECE_QUERY = 'from(bucket: "engine")\n' +
  '  |> range(start: v.timeRangeStart, stop: v.timeRangeStop)\n' +
  '  |> filter(fn: (r) => r["_measurement"] == "piece")\n' +
  '  |> filter(fn: (r) => r["_field"] == "process_start" or r["_field"] == "process_end" or r["_field"] == "process_duration")\n' +
  '  |> group()';

const ALERT_QUERY = 'from(bucket: "engine")\n' +
  '  |> range(start: v.timeRangeStart, stop: v.timeRangeStop)\n' +
  '  |> filter(fn: (r) => r["_measurement"] == "alert")\n' +
  '  |> filter(fn: (r) => r["_field"] == "type")\n' +
  '  |> group()';

export const VACAeroDashboardOperatorPerformance = () => {
  const [filters, setFilters] = useState(() => getVACAeroDashboardDefaultFilters([]));
  const [pieces, setPieces] = useState([]);
  const [alerts, setAlerts] = useState([]);
  const [page, setPage] = useState(1);
  const [isInfluxLoading, setIsInfluxLoading] = useState(true);

  const { data: patterns = [], isLoading: isPatternLoading } = useGetData('vacaero-dashboard-patterns', 'patterns');

  const pattern = useMemo(() => {
    if (!Array.isArray(patterns) || patterns.length === 0) return null;
    return patterns[0];
  }, [patterns]);

  useEffect(() => {
    let cancelled = false;

    const getData = async () => {
      setIsInfluxLoading(true);

      try {
        const pieceRows = await getInfluxdbData(
          'engine',
          'piece',
          [],
          [],
          HISTORY_START,
          'now()',
          '1ms',
          null,
          null,
          [],
          null,
          PIECE_QUERY,
          false,
          null,
        );

        const nextPieces = formatVACAeroDashboardInfluxPieces(pieceRows);
        const filteredPieces = filterVACAeroDashboardPieces(nextPieces, filters);

        let nextAlerts = [];

        if (filteredPieces.length > 0) {
          const selectedPieceIds = new Set(filteredPieces.map((piece) => String(piece.id)));

          const alertRows = await getInfluxdbData(
            'engine',
            'alert',
            [],
            [],
            HISTORY_START,
            'now()',
            '1ms',
            null,
            null,
            [],
            null,
            ALERT_QUERY,
            false,
            null,
          );

          nextAlerts = formatVACAeroDashboardInfluxAlerts(alertRows).filter((alert) => selectedPieceIds.has(String(alert.piece_id)));
        }

        if (cancelled) return;

        setPieces(nextPieces);
        setAlerts(nextAlerts);
      } catch {
        if (cancelled) return;

        setPieces([]);
        setAlerts([]);
      } finally {
        if (!cancelled) setIsInfluxLoading(false);
      }
    };

    getData();

    return () => {
      cancelled = true;
    };
  }, [filters]);

  const dashboardData = useMemo(() => {
    return getVACAeroDashboardData({
      pieces,
      alerts,
      pattern,
      filters,
    });
  }, [pieces, alerts, pattern, filters]);

  const handleFiltersChange = (nextFilters) => {
    setFilters(nextFilters);
    setPage(1);
  };

  const isLoading = isPatternLoading || isInfluxLoading;
  const hasData = Boolean(pattern) && dashboardData.processes.length > 0;
  const metrics = dashboardData.metrics;

  const badToolUsageText = hasData
    ? `${metrics.badToolUsageCount} ${metrics.badToolUsageCount > 1 ? 'utilisations d\'un outil incorrect' : 'utilisation d\'un outil incorrect'}`
    : null;

  const pieceCountText = hasData
    ? `${metrics.pieceCount} ${metrics.pieceCount > 1 ? 'pièces réalisées' : 'pièce réalisée'}`
    : null;

  const cycleTimeText = hasData && Number.isFinite(metrics.theoreticalCycleTime)
    ? `${pieceCountText} · Temps théorique : ${formatVACAeroDashboardDuration(metrics.theoreticalCycleTime)}`
    : pieceCountText;

  return (
    <Panel title="PERFORMANCE OPÉRATEUR" noPadding>
      <div className="flex w-full flex-col gap-4 p-4">
        {isLoading && <Loader text="Chargement..." />}

        <div className="text-xl text-perception-gray-600">
          <span className="font-medium text-perception-gray-300">{pattern?.name ?? '—'}</span>
        </div>

        <VACAeroDashboardFilters pieces={pieces} value={filters} onChange={handleFiltersChange} />

        {!isLoading && !hasData && <VACAeroDashboardNoDataNotice />}

        <div className="grid grid-cols-1 gap-4 xl:grid-cols-2 2xl:grid-cols-4">
          <VACAeroDashboardScoreKpi
            label="Indice de maîtrise du geste"
            value={hasData ? metrics.masteryIndex : null}
            displayValue={hasData ? formatVACAeroDashboardIndex(metrics.masteryIndex) : '—'}
            mediumThreshold={60}
            goodThreshold={80}
          />

          <VACAeroDashboardScoreKpi
            label="Taux de conformité opératoire"
            value={hasData ? metrics.complianceRate : null}
            displayValue={hasData ? formatVACAeroDashboardPercentage(metrics.complianceRate) : '—'}
            mediumThreshold={85}
            goodThreshold={95}
            subtitle={badToolUsageText}
          />

          <VACAeroDashboardKpi label="Nombre d'écarts qualité détectés" value={hasData ? metrics.alertCount : '—'} />

          <VACAeroDashboardKpi
            label="Temps de cycle moyen"
            value={hasData ? formatVACAeroDashboardDuration(metrics.averageCycleTime) : '—'}
            subtitle={cycleTimeText}
          />
        </div>

        <div className="grid grid-cols-1 gap-4 2xl:grid-cols-2">
          <VACAeroDashboardMasteryEvolutionChart evolution={hasData ? dashboardData.evolution : []} />

          <VACAeroDashboardCycleTimeEvolutionChart
            evolution={hasData ? dashboardData.evolution : []}
            theoreticalCycleTime={hasData ? metrics.theoreticalCycleTime : null}
          />
        </div>

        <div className="grid grid-cols-1 gap-4 2xl:grid-cols-2">
          <VACAeroDashboardComplianceEvolutionChart evolution={hasData ? dashboardData.evolution : []} />
          <VACAeroDashboardAlertDistribution distribution={hasData ? dashboardData.alertDistribution : []} />
        </div>

        <div className="flex flex-col gap-3">
          <div className="text-sm font-semibold uppercase text-perception-gray-400">
            Process récents
          </div>

          <VACAeroDashboardProcessesTable processes={hasData ? dashboardData.processes : []} page={page} onPageChange={setPage} />
        </div>
      </div>
    </Panel>
  );
};